import { useEffect, useState } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import type { NoticeItem } from '../../shared/data/constants';
import { drumLanguageAtom, drumSelectedNoticeAtom } from '../../shared/store/drumAppAtoms';
import { fetchPublicNotices } from '../../shared/api/memberApi';
import { formatProfileDate } from '../../shared/utils/drumHelpers';
import './Notices.scss';

function Notices() {
  const language = useAtomValue(drumLanguageAtom);
  const setSelectedNotice = useSetAtom(drumSelectedNoticeAtom);
  const navigate = useNavigate();
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchPublicNotices()
      .then((items) => {
        if (cancelled) return;
        setNotices(items);
        setErrorMessage('');
      })
      .catch((error) => {
        if (cancelled) return;
        setNotices([]);
        setErrorMessage(error instanceof Error ? error.message : (language === 'ko' ? '공지사항을 불러오지 못했습니다.' : 'Unable to load notices.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const openNotice = (notice: NoticeItem) => {
    setSelectedNotice(notice);
    navigate(`/drum/profile/notices/${notice.id}`);
  };

  return (
    <div className="page profile-subpage">
      <div className="page-intro"><span className="eyebrow">SUPPORT</span><h1>Notices</h1><p>Read the latest song, service and learning updates.</p></div>
      {isLoading ? (
        <p className="empty-list">Loading...</p>
      ) : errorMessage ? (
        <p className="empty-list">{errorMessage}</p>
      ) : notices.length === 0 ? (
        <p className="empty-list">No notices yet.</p>
      ) : (
        <div className="notice-list">
          {notices.map((notice) => (
            <button className="notice-item" key={notice.id} onClick={() => openNotice(notice)}>
              <span>{notice.tag}</span>
              <time>{formatProfileDate(notice.date, language)}</time>
              <h2>{notice.title}</h2>
              <p>{notice.summary}</p>
              <CaretRight />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default Notices;
